import { usePaywallStates } from '@/atoms/paywallAtoms';
import { setSidebarOpenTab } from '@/atoms/persistedPreferencesAtoms';
import { sidebarPanelContentAtom } from '@/atoms/sidebarPanelAtoms';
import { openedSessionTabAtom, openedSessionTabEnum } from '@/atoms/sideNavAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED } from '@/components/constants/flags';
import { ASSISTANT_VIEW_SELECT_ID } from '@/features/actionCenter/constants';
import { useCombinedOrganizationInfo } from '@/hooks/query/useCombinedOrganizationInfo';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { trackPaywallViewed } from '@/lib/analytics/scribe/payments';
import { PaywallSource } from '@/models/analytics/features/payments';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { RoleCategory, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useQuerySessionById } from '@heidi/session/data';
import { useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

export const useReviewCombobox = () => {
  const { data: userData } = useQueryUser((data) => ({
    kinde_user_id: data?.kinde_user_id,
  }));
  const setOpenedSessionTab = useSetAtom(openedSessionTabAtom);
  const setSidebarPanelContent = useSetAtom(sidebarPanelContentAtom);
  const onSelectUserChange = useCallback(
    (value: string) => {
      if (value === userData?.kinde_user_id) {
        return;
      }
      setOpenedSessionTab(openedSessionTabEnum.PAST);
    },
    [userData?.kinde_user_id, setOpenedSessionTab]
  );
  const router = useRouter();
  const [selectedReviewUser, setSelectedReviewUser] = useAtom(selectedReviewMemberAtom);
  const { teamDetails, isSupportRole } = useCombinedQueriesTeamGeneralInfo();
  const { isTogetherOrHigherTier } = useQueryUserTier();
  const { toggleUpgradeTogetherModal, togglePracticeUpgradeModal } = usePaywallStates();

  const handleUpgradeClick = useCallback(() => {
    if (IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED()) {
      togglePracticeUpgradeModal(true);
      trackPaywallViewed({
        source: PaywallSource.SessionReview,
        target_plan: SubscriptionTier.CLINICIAN_PRO_PLUS,
      });
    } else {
      toggleUpgradeTogetherModal(true);
      trackPaywallViewed({
        source: PaywallSource.SessionReview,
        target_plan: SubscriptionTier.PRO_PLUS,
      });
    }
  }, [togglePracticeUpgradeModal, toggleUpgradeTogetherModal]);
  const sessionId = useCurrentSessionId();
  const { data: currentSession } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        kinde_gp_id: data.kinde_gp_id,
      };
    },
  });
  const { isGlobalAdmin } = useCombinedOrganizationInfo();

  const gpId = currentSession?.kinde_gp_id;

  const onSelectReviewUser = (value: string) => {
    if (value === ASSISTANT_VIEW_SELECT_ID) {
      // View all is a static link: navigate only, don't change selected physician
      setSidebarOpenTab(undefined);
      setSidebarPanelContent(null);
      void router.push('/action-center');
      return;
    }
    onSelectUserChange(value);
    setSelectedReviewUser(value);
    void router.push('/scribe');
  };

  const userList = useMemo(() => {
    const acceptedUsers = teamDetails?.team.joined_team_members;
    // current user
    const currentUser = acceptedUsers?.find(
      (user) => user.kinde_user_id === userData?.kinde_user_id
    );
    if (acceptedUsers?.length === 1 && currentUser) {
      return acceptedUsers;
    }
    // create a list without current user
    const uniqueUsers = acceptedUsers?.filter(
      (user) =>
        user.kinde_user_id !== userData?.kinde_user_id &&
        !(
          user?.user_role_list?.length === 1 &&
          user?.user_role_list?.at(0)?.category === RoleCategory.Support
        )
    );
    // put current user at the first place
    if (currentUser && uniqueUsers) {
      uniqueUsers.unshift(currentUser);
    }
    // only show 5 users if in pro/free tier and avoid clickable
    return isTogetherOrHigherTier ? uniqueUsers : (uniqueUsers?.slice(0, 5) ?? []);
  }, [
    teamDetails?.team.joined_team_members,
    isTogetherOrHigherTier,
    userData?.kinde_user_id,
  ]);

  const { t } = useTranslations();
  const titleText = useMemo(() => {
    if (selectedReviewUser === ASSISTANT_VIEW_SELECT_ID) {
      return t('actionCenter.header');
    }
    if (selectedReviewUser === userData?.kinde_user_id) {
      return t('sessionReview.me');
    }
    return teamDetails?.team.team_members?.find(
      (user) => user.kinde_user_id === selectedReviewUser
    )?.name;
  }, [selectedReviewUser, userData?.kinde_user_id, teamDetails?.team.team_members, t]);

  return {
    userData,
    selectedReviewUser,
    setSelectedReviewUser,
    isTogetherOrHigherTier,
    isCurrentUserAssistant: isSupportRole,
    handleUpgradeClick,
    gpId,
    isGlobalAdmin,
    onSelectReviewUser,
    userList,
    titleText,
  };
};
